import { themes } from '@sede-enterprise/shell-ds-react-framework';
import {
  AllTradeMarkets,
  AncillaryMarket,
  BalancingMechanismMarket,
  ColorMapKey,
  LegendLabels,
  MarketKey,
  WholesaleMarket,
} from 'pages/Optimisation/components/ContractedVolume/types';

export enum MARKET_FILTER_VALUES {
  All = 'allMarkets',
  Ancillary = 'ancillary',
  BalancingMechanism = 'balancingMechanism',
  Wholesale = 'wholesale',
}

export enum TRADE_SIDE {
  SELL = 'sell',
  BUY = 'buy',
}

export const COLOR_MAP: Record<
  ColorMapKey,
  Partial<Record<AllTradeMarkets | 'disabled' | 'other', string>>
> = {
  ancillary: {
    dcLow: '#81CCD9',
    dcHigh: '#0097A9',
    dmLow: '#CAE4EE',
    dmHigh: '#69B4CE',
    drLow: '#4B78AA',
    drHigh: '#204B7A',
    positiveBr: '#8FADD1',
    negativeBr: '#D6E1EF',
    positiveQr: '#86207C',
    negativeQr: '#A65A9B',
    positiveSr: '#C39EC9',
    negativeSr: '#E9DBEB',
    // Same colors as those above.
    positiveQrUtilisation: '#86207C',
    negativeQrUtilisation: '#A65A9B',
    positiveSrUtilisation: '#C39EC9',
    negativeSrUtilisation: '#E9DBEB',
  } as Record<AncillaryMarket, string>,
  wholesale: {
    dayAheadHalfHourly: '#8FB753',
    dayAheadHourly: '#C9EC9A',
    intraday: '#008557',
    ida1: '#A8B11A',
    ida2: '#DDE599',
    systemPrice: '#97CDAF',
    otc: '#676233',
    longTermHedge: '#E1DDA9',
  } as Record<WholesaleMarket, string>,
  balancingMechanism: {
    balancingMechanismBid: '#FB594E',
    balancingMechanismOffer: '#DD1D21',
  } as Record<BalancingMechanismMarket, string>,
  other: {
    disabled: '#66799D1A',
  },
};

const getLegendLabels = (): LegendLabels => {
  return {
    [MARKET_FILTER_VALUES.All]: [
      {
        label: 'DC High',
        identifier: 'dcHigh',
        market: MarketKey.Ancillary,
      },
      { label: 'DC Low', identifier: 'dcLow', market: MarketKey.Ancillary },
      { label: 'DM High', identifier: 'dmHigh', market: MarketKey.Ancillary },
      { label: 'DM Low', identifier: 'dmLow', market: MarketKey.Ancillary },
      { label: 'DR High', identifier: 'drHigh', market: MarketKey.Ancillary },
      { label: 'DR Low', identifier: 'drLow', market: MarketKey.Ancillary },
      {
        label: 'Positive BR',
        identifier: 'positiveBr',
        market: MarketKey.Ancillary,
      },
      {
        label: 'Negative BR',
        identifier: 'negativeBr',
        market: MarketKey.Ancillary,
      },
      {
        label: 'Positive QR',
        identifier: 'positiveQr',
        market: MarketKey.Ancillary,
        groupKey: 'positiveQr',
      },
      {
        label: 'Negative QR',
        identifier: 'negativeQr',
        market: MarketKey.Ancillary,
        groupKey: 'negativeQr',
      },
      {
        label: 'Positive QR',
        exportLabel: 'Positive QR Utilisation',
        identifier: 'positiveQrUtilisation',
        market: MarketKey.Ancillary,
        groupKey: 'positiveQr',
      },
      {
        label: 'Negative QR',
        exportLabel: 'Negative QR Utilisation',
        identifier: 'negativeQrUtilisation',
        market: MarketKey.Ancillary,
        groupKey: 'negativeQr',
      },
      {
        label: 'Positive SR',
        identifier: 'positiveSr',
        market: MarketKey.Ancillary,
        groupKey: 'positiveSr',
      },
      {
        label: 'Negative SR',
        identifier: 'negativeSr',
        market: MarketKey.Ancillary,
        groupKey: 'negativeSr',
      },
      {
        label: 'Positive SR',
        exportLabel: 'Positive SR Utilisation',
        identifier: 'positiveSrUtilisation',
        market: MarketKey.Ancillary,
        groupKey: 'positiveSr',
      },
      {
        label: 'Negative SR',
        exportLabel: 'Negative SR Utilisation',
        identifier: 'negativeSrUtilisation',
        market: MarketKey.Ancillary,
        groupKey: 'negativeSr',
      },
      {
        label: 'Wholesale DA Hourly',
        identifier: 'dayAheadHourly',
        market: MarketKey.Wholesale,
      },
      {
        label: 'Wholesale DA Half Hourly',
        identifier: 'dayAheadHalfHourly',
        market: MarketKey.Wholesale,
      },
      {
        label: 'Wholesale Intraday',
        identifier: 'intraday',
        market: MarketKey.Wholesale,
        groupKey: 'intraday',
      },
      {
        label: 'Wholesale Intraday',
        identifier: 'ida1',
        market: MarketKey.Wholesale,
        groupKey: 'intraday',
      },
      {
        label: 'Wholesale Intraday',
        identifier: 'ida2',
        market: MarketKey.Wholesale,
        groupKey: 'intraday',
      },
      {
        label: 'Wholesale System Price',
        identifier: 'systemPrice',
        market: MarketKey.Wholesale,
      },
      {
        label: 'OTC',
        identifier: 'otc',
        market: MarketKey.Wholesale,
        groupKey: 'otc',
      },
      {
        label: 'OTC',
        identifier: 'longTermHedge',
        market: MarketKey.Wholesale,
        groupKey: 'otc',
      },

      {
        label: 'Balancing Mechanism offer',
        market: MarketKey.BalancingMechanism,
        identifier: 'balancingMechanismOffer',
      },
      {
        label: 'Balancing Mechanism bid',
        market: MarketKey.BalancingMechanism,
        identifier: 'balancingMechanismBid',
      },
    ],
    [MARKET_FILTER_VALUES.Ancillary]: [
      {
        label: 'DC High',
        identifier: 'dcHigh',
        market: MarketKey.Ancillary,
      },
      { label: 'DC Low', identifier: 'dcLow', market: MarketKey.Ancillary },
      { label: 'DM High', identifier: 'dmHigh', market: MarketKey.Ancillary },
      { label: 'DM Low', identifier: 'dmLow', market: MarketKey.Ancillary },
      { label: 'DR High', identifier: 'drHigh', market: MarketKey.Ancillary },
      { label: 'DR Low', identifier: 'drLow', market: MarketKey.Ancillary },
      {
        label: 'Positive BR',
        identifier: 'positiveBr',
        market: MarketKey.Ancillary,
      },
      {
        label: 'Negative BR',
        identifier: 'negativeBr',
        market: MarketKey.Ancillary,
      },
      {
        label: 'Positive QR',
        identifier: 'positiveQr',
        market: MarketKey.Ancillary,
        groupKey: 'positiveQr',
      },
      {
        label: 'Negative QR',
        identifier: 'negativeQr',
        market: MarketKey.Ancillary,
        groupKey: 'negativeQr',
      },
      {
        label: 'Positive QR',
        exportLabel: 'Positive QR Utilisation',
        identifier: 'positiveQrUtilisation',
        market: MarketKey.Ancillary,
        groupKey: 'positiveQr',
      },
      {
        label: 'Negative QR',
        exportLabel: 'Negative QR Utilisation',
        identifier: 'negativeQrUtilisation',
        market: MarketKey.Ancillary,
        groupKey: 'negativeQr',
      },
      {
        label: 'Positive SR',
        identifier: 'positiveSr',
        market: MarketKey.Ancillary,
        groupKey: 'positiveSr',
      },
      {
        label: 'Negative SR',
        identifier: 'negativeSr',
        market: MarketKey.Ancillary,
        groupKey: 'negativeSr',
      },
      {
        label: 'Positive SR',
        exportLabel: 'Positive SR Utilisation',
        identifier: 'positiveSrUtilisation',
        market: MarketKey.Ancillary,
        groupKey: 'positiveSr',
      },
      {
        label: 'Negative SR',
        exportLabel: 'Negative SR Utilisation',
        identifier: 'negativeSrUtilisation',
        market: MarketKey.Ancillary,
        groupKey: 'negativeSr',
      },
      {
        label: 'Wholesale and/or Balancing Mechanism',
        identifier: 'disabled',
        market: 'other',
      },
    ],
    [MARKET_FILTER_VALUES.BalancingMechanism]: [
      {
        label: 'Balancing Mechanism offer',
        market: MarketKey.BalancingMechanism,
        identifier: 'balancingMechanismOffer',
      },
      {
        label: 'Balancing Mechanism bid',
        market: MarketKey.BalancingMechanism,
        identifier: 'balancingMechanismBid',
      },
      {
        label: 'Ancillary and/or Wholesale',
        identifier: 'disabled',
        market: 'other',
      },
    ],
    [MARKET_FILTER_VALUES.Wholesale]: [
      {
        label: 'DA Half-Hourly',
        identifier: 'dayAheadHalfHourly',
        market: MarketKey.Wholesale,
      },
      {
        label: 'DA Hourly',
        identifier: 'dayAheadHourly',
        market: MarketKey.Wholesale,
      },
      {
        label: 'Intraday',
        identifier: 'intraday',
        market: MarketKey.Wholesale,
        groupKey: 'intraday',
      },
      {
        label: 'System Price',
        identifier: 'systemPrice',
        market: MarketKey.Wholesale,
      },
      {
        label: 'OTC',
        identifier: 'otc',
        market: MarketKey.Wholesale,
        groupKey: 'otc',
      },
      {
        label: 'OTC',
        identifier: 'longTermHedge',
        market: MarketKey.Wholesale,
        groupKey: 'otc',
      },
      {
        label: 'Ancillary and/or Balancing Mechanism',
        identifier: 'disabled',
        market: 'other',
      },
    ],
  } satisfies LegendLabels;
};

export const CHART_CONSTANTS = {
  lightModeDisabledColor: '#f0f2f6',
  darkModeDisabledColor: themes.dark.text.onSurface.inactive,
  fallbackColor: '#ffffff',
  colorMap: COLOR_MAP,
  legendLabels: getLegendLabels(),
  marketFilters: [
    { display: 'All markets', value: MARKET_FILTER_VALUES.All },
    { display: 'Ancillary', value: MARKET_FILTER_VALUES.Ancillary },
    { display: 'Wholesale', value: MARKET_FILTER_VALUES.Wholesale },
    {
      display: 'Balancing Mechanism',
      value: MARKET_FILTER_VALUES.BalancingMechanism,
    },
  ],
  efaLength: 4, // hours
  efaBlocks: 6, // per day
  efaOffset: 2, // hours from first settlement period
};

export const WHOLESALE_MARKET_STACKING_ORDER = {
  dayAheadHourly: 1,
  dayAheadHalfHourly: 2,
  intraday: 3,
  systemPrice: 4,
};
